import { Box, IconButton, TextField, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { MdSend } from "react-icons/md";
import { useDeviceType } from "../../hooks/useDeviceType";
import IconWrapper from "../Common/IconWrapper";
import SubmitShortcutHint from "../Common/SubmitShortcutHint";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
}

const SendIcon = IconWrapper(MdSend);

/**
 * **QuestionInput Component**
 *
 * Renders a text input field where users can type and send questions.
 * Supports auto-expanding for multi-line input and disables submission when an answer is loading.
 *
 * @component
 * @param {QuestionInputProps} props - The props for the component.
 * @returns {JSX.Element} A styled input field with a send button.
 *
 * @example
 *
 *  <QuestionInput
 *    placeholder="Ask a question..."
 *    onSend={handleSendQuestion}
 *  />
 */
export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend }: Props) => {
    const theme = useTheme();
    const { isDesktop } = useDeviceType();
    const [question, setQuestion] = useState<string>("");
    const textFieldRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        if (!textFieldRef.current) return;

        textFieldRef.current.focus();
    }, []);

    // Auto-expand the text area height
    useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.style.height = "auto";
            textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`;
        }
    }, [question]);

    const sendQuestion = () => {
        if (disabled || !question.trim()) return;

        onSend(question);

        if (clearOnSend) setQuestion("");
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();

            if (textFieldRef.current) textFieldRef.current.focus();
        }
    };

    const onQuestionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        if (newValue.length <= 2000) {
            setQuestion(newValue);
        }
    };

    const sendQuestionDisabled = disabled || !question.trim();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "space-between",
                width: "100%",
                height: "auto",
                padding: "4px",
                borderRadius: "16px",
                background: theme.palette.cardBackground,
                boxShadow: "0px 8px 8px rgba(0,0,0,0.2)",
                border: `2px solid ${theme.palette.divider}`,
                margin: "auto",
                position: "relative"
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    height: "auto"
                }}
            >
                <TextField
                    inputRef={textFieldRef}
                    sx={{
                        flexGrow: 1,
                        maxHeight: "13rem", // 10 lines
                        overflow: "auto",

                        "&::-webkit-scrollbar": {
                            width: "4px",
                            backgroundColor: "transparent"
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: theme.palette.scrollbarThumb,
                            borderRadius: "4px",
                            "&:hover": {
                                backgroundColor: theme.palette.scrollbarThumb
                            }
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: theme.palette.scrollbarTrack,
                            borderRadius: "4px"
                        },
                        // Firefox
                        scrollbarWidth: "thin",
                        scrollbarColor: `${theme.palette.scrollbarThumb} ${theme.palette.scrollbarTrack}`,

                        borderRadius: "8px",
                        background: theme.palette.cardBackground,

                        "& .MuiInputBase-root": {
                            padding: 0,
                            fontSize: "1rem"
                        },
                        "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
                            p: 0,
                            minHeight: "unset",
                            alignItems: "flex-start"
                        },

                        "& textarea.MuiInputBase-input:placeholder-shown": {
                            height: "auto !important"
                        },

                        /* Textarea (actual input) */
                        "& .MuiInputBase-input": {
                            padding: "12px 0px 0px 12px",
                            resize: "none",
                            height: "auto",
                            fontSize: "0.9375rem",
                            lineHeight: "1.25rem",
                            fontFamily: "inherit",
                            "&::placeholder": {
                                opacity: 0.6
                            }
                        },

                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none"
                        }
                    }}
                    placeholder={placeholder}
                    multiline
                    fullWidth
                    variant="outlined"
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    onPaste={e => {
                        const pasted = e.clipboardData.getData("text");
                        const current = question;

                        const el = textFieldRef.current;
                        if (!el) return; // fallback to default paste behavior

                        const start = el.selectionStart;
                        const end = el.selectionEnd;

                        const before = current.slice(0, start);
                        const after = current.slice(end);

                        const combined = before + pasted + after;

                        setQuestion(combined);
                        e.preventDefault(); // prevent default paste to avoid double insert
                    }}
                    minRows={1}
                    maxRows={10} // Prevents excessive growth
                />
                <IconButton
                    sx={{
                        marginLeft: "8px",
                        transition: "opacity 0.2s",
                        opacity: sendQuestionDisabled ? 0.4 : 1
                    }}
                    onClick={sendQuestion}
                    disabled={sendQuestionDisabled}
                    aria-label="Send message"
                >
                    <SendIcon
                        style={{
                            fill: sendQuestionDisabled
                                ? theme.palette.action.disabled
                                : theme.palette.mode === "dark"
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.main
                        }}
                    />
                </IconButton>
            </Box>
            {isDesktop && <SubmitShortcutHint />}
        </Box>
    );
};
